import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { endpoints } from '../../environments/endpoints';
import { ApiService } from '../Services/api.service';
import { AuthFacade } from './auth.facade';
import { ResourceStateService } from '../Services/resource-state.service';
import { TypedResourceRequest } from '../Interfaces/api.interface';
import { LaravelDefaultResponse } from '../Interfaces/response.interface';
import { OnboardingContract } from '../Interfaces/onboarding.interface';
import { CompanyData } from '../Interfaces/company-data.interface';
import { UpdateCompanyDataPayload } from '../Interfaces/update-company-data-payload.interface';
import { SavePlanPayload } from '../Interfaces/save-plan-payload.interface';
import { CompleteOnboardingPayload } from '../Interfaces/complete-onboarding-payload.interface';
import { CompleteOnboardingResult } from '../Interfaces/complete-onboarding-result.interface';
import { AdminConsentRedirect } from '../Interfaces/admin-consent-redirect.interface';
import { OnboardingConfig } from '../Interfaces/onboarding-config.interface';
import { SalesContactPayload } from '../Interfaces/sales-contact-payload.interface';
import {
  ConfigurationChoiceAvailability,
  ConfigurationChoicePayload,
} from '../Interfaces/configuration-choice.interface';
import { ConfigurationChoice } from '../Enums/configuration-choice.enum';
import { Onboarding } from '../Models/onboarding.model';
import { CustomOfferTokenService } from '../Services/custom-offer-token.service';
import { CustomOfferRedemption } from '../Interfaces/custom-offer-redemption.interface';
import { RedeemCustomOfferPayload } from '../Interfaces/redeem-custom-offer-payload.interface';

@Injectable({
  providedIn: 'root',
})
export class OnboardingFacade {
  private readonly _api = inject(ApiService);
  private readonly _authFacade = inject(AuthFacade);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _customOfferToken = inject(CustomOfferTokenService);

  private readonly _onboarding = signal<Onboarding | null>(null);
  readonly onboarding = this._onboarding.asReadonly();

  private readonly _configurationChoiceApplies = signal(false);
  readonly configurationChoiceApplies = this._configurationChoiceApplies.asReadonly();

  readonly status = computed(() => this._onboarding()?.status ?? null);
  readonly hasConsent = computed(() => this._onboarding()?.hasConsent() ?? false);
  readonly hasSelectedPlan = computed(() => this._onboarding()?.hasSelectedPlan() ?? false);

  private readonly _configResource = httpResource<LaravelDefaultResponse<OnboardingConfig>>(() =>
    this._authFacade.isAuthenticated()
      ? this._api.get<LaravelDefaultResponse<OnboardingConfig>>(endpoints.onboarding.config)
      : undefined,
  );

  readonly config = computed(() => this._resourceState.valueOf(this._configResource)?.data ?? null);
  readonly configLoading = computed(() => this._configResource.isLoading());
  readonly configError = computed(() => this._configResource.error());

  reloadConfig(): void {
    this._configResource.reload();
  }

  createSession(): Observable<Onboarding> {
    const request = this._api.post<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.session,
    );
    return this._api.run<LaravelDefaultResponse<OnboardingContract>>(request).pipe(
      map((response) => new Onboarding(response.data)),
      switchMap((onboarding) => this.redeemStoredCustomOffer(onboarding)),
      tap((onboarding) => this._onboarding.set(onboarding)),
    );
  }

  clearCustomOfferToken(): void {
    this._customOfferToken.clear();
  }

  reclaimSessionWithHandoff(): Observable<Onboarding> {
    const request = this._api.post<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.session,
      { withCredentials: true },
    );
    return this.runAndStore(request);
  }

  loadOnboarding(): Observable<Onboarding> {
    const request = this._api.get<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.get,
    );
    return this.runAndStore(request);
  }

  saveCompanyData(companyData: CompanyData): Observable<Onboarding> {
    const payload: UpdateCompanyDataPayload = { company_data: companyData, onboarder_data: {} };
    const request = this._api.put<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.update,
      {
        body: payload,
      },
    );
    return this.runAndStore(request);
  }

  savePlanSelection(payload: SavePlanPayload): Observable<Onboarding> {
    const request = this._api.patch<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.savePlan,
      {
        body: payload,
      },
    );
    return this.runAndStore(request);
  }

  submitSalesContact(payload: SalesContactPayload): Observable<Onboarding> {
    const request = this._api.post<LaravelDefaultResponse<OnboardingContract>>(
      endpoints.onboarding.salesContact,
      {
        body: payload,
      },
    );
    return this.runAndStore(request);
  }

  refreshConfigurationChoice(): Observable<boolean> {
    const request = this._api.get<LaravelDefaultResponse<ConfigurationChoiceAvailability>>(
      endpoints.onboarding.configurationChoice,
      { skipError: true, skipLoading: true },
    );
    return this._api.run<LaravelDefaultResponse<ConfigurationChoiceAvailability>>(request).pipe(
      map((response) => response.data.applies),
      tap((applies) => this._configurationChoiceApplies.set(applies)),
    );
  }

  applyConfigurationChoice(choice: ConfigurationChoice): Observable<void> {
    const payload: ConfigurationChoicePayload = { choice };
    const request = this._api.post<unknown>(endpoints.onboarding.configurationChoice, {
      body: payload,
    });
    return this._api.run<unknown>(request).pipe(map(() => undefined));
  }

  requestAdminConsentRedirect(): Observable<string> {
    const request = this._api.get<LaravelDefaultResponse<AdminConsentRedirect>>(
      endpoints.onboarding.adminConsentRedirect,
    );
    return this._api
      .run<LaravelDefaultResponse<AdminConsentRedirect>>(request)
      .pipe(map((response) => response.data.consent_url));
  }

  completeOnboarding(payload: CompleteOnboardingPayload): Observable<CompleteOnboardingResult> {
    const request = this._api.post<LaravelDefaultResponse<CompleteOnboardingResult>>(
      endpoints.onboarding.complete,
      { body: payload, withCredentials: true },
    );
    return this._api
      .run<LaravelDefaultResponse<CompleteOnboardingResult>>(request)
      .pipe(map((response) => response.data));
  }

  private redeemStoredCustomOffer(onboarding: Onboarding): Observable<Onboarding> {
    const token = this._customOfferToken.read();
    if (!token) {
      return of(onboarding);
    }

    const payload: RedeemCustomOfferPayload = { token };
    const request = this._api.post<LaravelDefaultResponse<CustomOfferRedemption>>(
      endpoints.onboarding.customOffer,
      { body: payload, skipError: true, skipLoading: true },
    );
    return this._api.run<LaravelDefaultResponse<CustomOfferRedemption>>(request).pipe(
      map((response) => {
        if (!response.data.applied) {
          this._customOfferToken.clear();
          return onboarding;
        }
        return new Onboarding(response.data.onboarding);
      }),
      catchError(() => {
        this._customOfferToken.clear();
        return of(onboarding);
      }),
    );
  }

  private runAndStore(
    request: TypedResourceRequest<LaravelDefaultResponse<OnboardingContract>>,
  ): Observable<Onboarding> {
    return this._api.run<LaravelDefaultResponse<OnboardingContract>>(request).pipe(
      map((response) => new Onboarding(response.data)),
      tap((onboarding) => this._onboarding.set(onboarding)),
    );
  }
}
